import { CheckCircle, ClipboardCopy, Dices, Eye, EyeOff } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	generatePassphrase,
	getStrength,
	type PassphraseOptions,
	type Separator,
} from "@/lib/passphrase.ts";
import { cn } from "@/lib/utils.ts";
import "./index.css";

const SEPARATORS: readonly Separator[] = [
	"-",
	".",
	"_",
	"!",
	"?",
	" ",
] as const;

const SEPARATOR_LABELS: Record<Separator, string> = {
	"-": "-",
	".": ".",
	_: "_",
	"!": "!",
	"?": "?",
	" ": "␣",
};

const DEFAULT_OPTIONS: PassphraseOptions = {
	wordCount: 6,
	capitalize: true,
	includeNumber: true,
	separator: "-",
};

const MIN_WORDS = 3;
const MAX_WORDS = 12;

export function App() {
	const [options, setOptions] = useState<PassphraseOptions>(DEFAULT_OPTIONS);
	const [passphrase, setPassphrase] = useState("");
	const [visible, setVisible] = useState(true);
	const [copied, setCopied] = useState(false);

	const strength = useMemo(() => getStrength(options), [options]);

	const regenerate = useCallback(() => {
		setPassphrase(generatePassphrase(options));
		setCopied(false);
	}, [options]);

	useEffect(() => {
		regenerate();
	}, [regenerate]);

	const handleCopy = async () => {
		await navigator.clipboard.writeText(passphrase);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	const updateOption = <K extends keyof PassphraseOptions>(
		key: K,
		value: PassphraseOptions[K],
	) => {
		setOptions((prev) => ({ ...prev, [key]: value }));
	};

	const maskedPassphrase = visible
		? passphrase
		: passphrase.replace(/[^\s\-._!?]/g, "•");

	return (
		<div className="min-h-screen w-full flex flex-col relative z-10">
			<div className="animated-bg" />

			{/* Hero Section */}
			<div className="py-16 px-4 text-center text-slate-100">
				<div className="inline-flex items-center justify-center p-3 bg-emerald-500/10 rounded-2xl mb-6 border border-emerald-500/20 shadow-[0_0_30px_rgba(16,185,129,0.15)]">
					<Dices className="size-8 text-emerald-400" />
				</div>
				<h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4 font-display bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-cyan-400">
					Passphrase Generator
				</h1>
				<p className="text-lg md:text-xl text-slate-400 max-w-xl mx-auto leading-relaxed font-light">
					Create cryptographically strong, memorable passphrases that keep your
					digital life secure.
				</p>
			</div>

			{/* Main Card */}
			<div className="flex-1 flex items-start justify-center px-4 pb-12">
				<div className="bg-slate-900/60 backdrop-blur-xl border border-slate-800/60 rounded-3xl shadow-2xl w-full max-w-2xl p-6 md:p-8 space-y-8 relative overflow-hidden">
					{/* Decorative glow */}
					<div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />

					{/* Passphrase Display */}
					<div className="flex items-center gap-3">
						<div className="flex-1 relative group">
							<div className="passphrase-display border border-slate-700/50 rounded-xl px-5 py-4 text-lg md:text-2xl font-mono bg-slate-950/50 text-emerald-400 min-h-[72px] flex items-center break-all pr-14 select-all shadow-inner transition-colors group-hover:border-emerald-500/30">
								{maskedPassphrase}
							</div>
							<button
								type="button"
								onClick={() => setVisible((v) => !v)}
								className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-emerald-400 transition-colors p-1"
								aria-label={visible ? "Hide passphrase" : "Show passphrase"}
							>
								{visible ? (
									<EyeOff className="size-5" />
								) : (
									<Eye className="size-5" />
								)}
							</button>
						</div>
						<button
							type="button"
							onClick={regenerate}
							className="p-4 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 transition-all text-slate-300 hover:text-emerald-400 hover:border-emerald-500/30 hover:shadow-[0_0_15px_rgba(16,185,129,0.1)] shrink-0 active:scale-95"
							aria-label="Generate new passphrase"
						>
							<Dices className="size-6" />
						</button>
					</div>

					{/* Strength Bar */}
					<div className="space-y-3 bg-slate-950/30 p-4 rounded-2xl border border-slate-800/50">
						<div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
							<div
								className="h-full rounded-full transition-all duration-500 ease-out relative"
								style={{
									width: `${strength.percent}%`,
									backgroundColor: strength.color,
									boxShadow: `0 0 10px ${strength.color}`,
								}}
							/>
						</div>
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-2">
								<CheckCircle
									className="size-4"
									style={{ color: strength.color }}
								/>
								<span
									className="font-medium text-sm tracking-wide uppercase"
									style={{ color: strength.color }}
								>
									{strength.level}
								</span>
							</div>
							<button
								type="button"
								onClick={handleCopy}
								className={cn(
									"flex items-center gap-2 px-5 py-2 rounded-lg font-medium text-sm transition-all duration-200 active:scale-95",
									copied
										? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/50"
										: "bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 hover:border-slate-600",
								)}
							>
								<ClipboardCopy className="size-4" />
								{copied ? "Copied!" : "Copy"}
							</button>
						</div>
					</div>

					<div className="h-px w-full bg-gradient-to-r from-transparent via-slate-800 to-transparent" />

					{/* Word Count Slider */}
					<div className="space-y-4">
						<div className="flex justify-between items-center">
							<span className="text-sm font-medium text-slate-400">
								Word Count
							</span>
							<span className="text-sm font-bold text-emerald-400 bg-emerald-500/10 px-2.5 py-0.5 rounded-md border border-emerald-500/20">
								{options.wordCount}
							</span>
						</div>
						<input
							type="range"
							min={MIN_WORDS}
							max={MAX_WORDS}
							value={options.wordCount}
							onChange={(e) =>
								updateOption("wordCount", Number(e.target.value))
							}
							className="slider w-full"
						/>
					</div>

					{/* Options Row */}
					<div className="flex flex-wrap items-center justify-between gap-6 pt-2">
						<div className="flex gap-6">
							{/* Capitalize Toggle */}
							<label className="flex items-center gap-3 cursor-pointer select-none group">
								<input
									type="checkbox"
									checked={options.capitalize}
									onChange={(e) => updateOption("capitalize", e.target.checked)}
									className="checkbox"
								/>
								<span className="text-sm font-medium text-slate-300 group-hover:text-slate-100 transition-colors">
									Capitals
								</span>
							</label>

							{/* Numbers Toggle */}
							<label className="flex items-center gap-3 cursor-pointer select-none group">
								<input
									type="checkbox"
									checked={options.includeNumber}
									onChange={(e) =>
										updateOption("includeNumber", e.target.checked)
									}
									className="checkbox"
								/>
								<span className="text-sm font-medium text-slate-300 group-hover:text-slate-100 transition-colors">
									Numbers
								</span>
							</label>
						</div>

						{/* Separator Buttons */}
						<div className="flex items-center gap-3">
							<span className="text-sm font-medium text-slate-400">
								Separator
							</span>
							<div className="flex gap-1 bg-slate-950/50 p-1 rounded-lg border border-slate-800/50">
								{SEPARATORS.map((sep) => (
									<button
										key={sep}
										type="button"
										onClick={() => updateOption("separator", sep)}
										className={cn(
											"w-8 h-8 rounded-md text-sm font-bold transition-all duration-200 flex items-center justify-center",
											options.separator === sep
												? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 shadow-[0_0_10px_rgba(16,185,129,0.1)]"
												: "text-slate-400 hover:text-slate-200 hover:bg-slate-800 border border-transparent",
										)}
									>
										{SEPARATOR_LABELS[sep]}
									</button>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default App;
